import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  AlertCircle,
  User,
  Calendar,
  Clock,
  FileText,
  Save,
  Sparkles,
} from "lucide-react";
import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

interface FinalizationStepProps {
  onSave: () => void;
  onBack: () => void;
  loading?: boolean;
}


const FinalizationStep: React.FC<FinalizationStepProps> = ({
  onSave,
  onBack,
  loading,
}) => {
  const { formData } = useConsultationWizardState();
  const [isAnimating, setIsAnimating] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Animation d'entrée
    const timer = setTimeout(() => setIsAnimating(false), 1000);

    // Mise à jour de l'heure en temps réel
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const getPatientName = (): string => {
    return (
      `${formData.patientInfo.prenom} ${formData.patientInfo.nom}`.trim() ||
      "Non spécifié"
    );
  };

  const isPreEmploymentMotif = (): boolean => {
    return (
      formData.patientInfo.motifConsultation
        .toLowerCase()
        .includes("pré-embauche") ||
      formData.patientInfo.motifConsultation
        .toLowerCase()
        .includes("pre-embauche") ||
      formData.patientInfo.motifConsultation.toLowerCase().includes("embauche")
    );
  };

  // Date et heure actuelles pour le résumé
  const currentDateTime = currentTime.toLocaleString("fr-FR", {
    timeZone: "GMT",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "Non spécifiée";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-8 min-h-screen flex flex-col justify-center py-8">
      {/* Particules animées en arrière-plan */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-2 h-2 bg-green-200 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-40 right-32 w-1 h-1 bg-teal-200 rounded-full animate-bounce opacity-40"></div>
        <div className="absolute bottom-32 left-16 w-1.5 h-1.5 bg-emerald-200 rounded-full animate-ping opacity-30"></div>
        <div className="absolute bottom-20 right-20 w-1 h-1 bg-green-300 rounded-full animate-pulse opacity-50"></div>
      </div>

      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6">
        {/* Animation de succès */}
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-6 sm:p-8 rounded-3xl shadow-2xl border border-green-100 relative overflow-hidden">
          {/* Effet de brillance */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400"></div>

          {/* Icône de succès animée */}
          <div className="flex items-center justify-center mb-8">
            <div className={`relative ${isAnimating ? "animate-bounce" : ""}`}>
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-25"></div>
              <div className="relative bg-gradient-to-br from-green-400 to-green-600 p-4 rounded-full shadow-lg">
                <CheckCircle className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 text-white animate-pulse" />
              </div>
            </div>
          </div>

          {/* Titre avec effet de gradient */}
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
              Consultation Terminée
            </h3>
            <p className="text-lg text-gray-600 font-medium">
              Toutes les informations ont été enregistrées avec succès
            </p>
          </div>

          {/* Carte de résumé élégante */}
          <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-white/50 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
                <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-xl mr-3">
                  <FileText className="text-blue-600" size={24} />
                </div>
                Résumé de la Consultation
              </h4>
              <div className="flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                <Sparkles className="mr-1" size={12} />
                Finalisée
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <User className="text-blue-600 mr-3" size={20} />
                  <div>
                    <span className="text-sm font-medium text-blue-800">
                      Patient
                    </span>
                    <p className="text-lg font-bold text-blue-900">
                      {getPatientName()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <Calendar className="text-purple-600 mr-3" size={20} />
                  <div>
                    <span className="text-sm font-medium text-purple-800">
                      Date de consultation
                    </span>
                    <p className="text-lg font-bold text-purple-900">
                      {formatDate(formData.consultationActuelle.date)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                  <Clock className="text-green-600 mr-3" size={20} />
                  <div>
                    <span className="text-sm font-medium text-green-800">
                      Heure de finalisation
                    </span>
                    <p className="text-lg font-bold text-green-900">
                      {currentDateTime}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                  <span className="text-sm font-medium text-amber-800 flex items-center mb-1">
                    <FileText className="mr-2" size={16} />
                    Motif de consultation
                  </span>
                  <p className="text-base font-semibold text-amber-900">
                    {formData.patientInfo.motifConsultation || "Non spécifié"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Alerte pré-embauche modernisée */}
          {isPreEmploymentMotif() && (
            <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-200 p-6 rounded-2xl mb-8 shadow-lg">
              <div className="flex items-start">
                <div className="bg-amber-100 p-2 rounded-xl mr-4">
                  <AlertCircle className="h-6 w-6 text-amber-600 animate-pulse" />
                </div>
                <div className="flex-1">
                  <h5 className="text-amber-900 font-bold text-lg mb-2">
                    Consultation de Pré-Embauche Détectée
                  </h5>
                  <p className="text-amber-800 leading-relaxed">
                    Après l'enregistrement de cette consultation, vous serez
                    automatiquement dirigé vers la génération du certificat
                    médical de pré-embauche. Le document sera préparé selon les
                    normes en vigueur.
                  </p>
                  <div className="mt-3 p-3 bg-amber-100/50 rounded-lg">
                    <p className="text-sm text-amber-700 font-medium">
                      ✓ Certificat médical automatique
                      <br />
                      ✓ Conforme aux réglementations
                      <br />✓ Prêt à imprimer
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bouton d'action principal */}
          <div className="flex justify-center gap-6 mb-6">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-2xl font-semibold shadow hover:bg-gray-300 transition"
            >
              Retour à la saisie
            </button>
            <button
              onClick={onSave}
              className="group relative flex items-center px-10 py-4 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 active:scale-95 font-bold text-lg"
              disabled={loading}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-700 via-emerald-700 to-teal-700 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center">
                <Save className="mr-3 group-hover:animate-bounce" size={24} />
                Enregistrer la Consultation
                <div className="ml-3 w-2 h-2 bg-white rounded-full animate-ping"></div>
              </div>
            </button>
          </div>

          {/* Message informatif stylisé */}
          <div className="text-center">
            <div className="inline-flex items-center px-6 py-3 bg-white/60 backdrop-blur-sm rounded-full border border-white/40">
              <CheckCircle className="mr-2 text-green-600" size={16} />
              <span className="text-gray-700 font-medium">
                Le dossier médical du patient sera automatiquement mis à jour
              </span>
            </div>
          </div>
        </div>

        {/* Statistiques de la session (optionnel) */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/50 text-center">
            <div className="text-2xl font-bold text-blue-600">✓</div>
            <div className="text-sm text-gray-600 font-medium">
              Données complètes
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/50 text-center">
            <div className="text-2xl font-bold text-green-600">🔒</div>
            <div className="text-sm text-gray-600 font-medium">Sécurisé</div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/50 text-center">
            <div className="text-2xl font-bold text-purple-600">⚡</div>
            <div className="text-sm text-gray-600 font-medium">Synchronisé</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalizationStep;
